import { ORDER_CREATE_PREFILL_PARAM_KEYS } from './order-create-prefill';

/** Query flag that opens the create-order drawer on the orders list. */
export const ORDER_CREATE_DRAWER_PARAM = 'create';

/** Prefill keys forwarded from Properties / Opportunities legacy `/orders?…` bookmarks. */
export const ORDER_CREATE_INTENT_PARAM_KEYS = [
  'buildingId',
  'buildingName',
  'tenantName',
  'suite',
  'source',
  'changesRequested',
  'serviceTypeId',
] as const;

const ORDER_LIST_FILTER_PARAM_KEYS = [
  'view',
  'q',
  'status',
  'serviceType',
  'companyIds',
  'assignedToUserIds',
  'priority',
  'subscription',
  'dateFrom',
  'dateTo',
  'sort',
] as const;

/** Whether the URL currently carries list-filter params for `/orders`. */
export function hasOrderListFilterParams(searchParams: URLSearchParams): boolean {
  return ORDER_LIST_FILTER_PARAM_KEYS.some((key) => searchParams.has(key));
}

/**
 * True when `/orders?…` carries legacy create shortcuts (not list filters).
 * `serviceType` is omitted — the list filter bar uses the same key.
 */
export function hasOrderCreateIntentParams(searchParams: URLSearchParams): boolean {
  return ORDER_CREATE_INTENT_PARAM_KEYS.some((key) => searchParams.has(key));
}

/** Whether the create-order drawer should be open for the current list URL. */
export function isOrderCreateDrawerOpen(searchParams: URLSearchParams): boolean {
  if (searchParams.get(ORDER_CREATE_DRAWER_PARAM) === '1') return true;
  if (hasOrderListFilterParams(searchParams)) return false;
  return hasOrderCreateIntentParams(searchParams);
}

/**
 * Removes drawer + prefill params while preserving list filter state (`view`, `q`,
 * `serviceType`, etc.).
 */
export function stripOrderCreateDrawerParams(searchParams: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(searchParams);
  next.delete(ORDER_CREATE_DRAWER_PARAM);
  for (const key of ORDER_CREATE_INTENT_PARAM_KEYS) {
    next.delete(key);
  }
  for (const key of ORDER_CREATE_PREFILL_PARAM_KEYS) {
    if (key === 'serviceType') continue;
    next.delete(key);
  }
  return next;
}

/** Opens the drawer on the current list URL, optionally merging prefill params. */
export function mergeOrderCreateDrawerOpenParams(
  searchParams: URLSearchParams,
  prefill?: Record<string, string>,
): URLSearchParams {
  const next = stripOrderCreateDrawerParams(searchParams);
  next.set(ORDER_CREATE_DRAWER_PARAM, '1');
  if (prefill) {
    for (const [key, value] of Object.entries(prefill)) {
      if (value.trim()) next.set(key, value);
    }
  }
  return next;
}

/**
 * Copies create-drawer query params from `source` onto `target` so list-filter
 * rehydration does not drop an open drawer (Order 2.0 parity).
 */
export function preserveOrderCreateDrawerParams(
  target: URLSearchParams,
  source: URLSearchParams,
): URLSearchParams {
  if (!isOrderCreateDrawerOpen(source)) return target;
  if (source.get(ORDER_CREATE_DRAWER_PARAM) === '1') {
    target.set(ORDER_CREATE_DRAWER_PARAM, '1');
  }
  for (const key of ORDER_CREATE_INTENT_PARAM_KEYS) {
    const value = source.get(key);
    if (value != null && value !== '') target.set(key, value);
  }
  return target;
}
